import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { ArrowIcon } from "@/components/icons/fundred-icons";
import { useRef, useState } from "react";

const FOREST = "#0A1628";
const FOREST_MID = "#3B82F6";
const FOREST_DEEP = "#050D1A";
const GOLD = "#F59E0B";
const INK = "#0A1628";
const PAPER = "#F8FAFC";
const LINE = "#CBD5E1";
const MUTED = "#64748B";
const AQUA = "#3B82F6";
const CORAL = "#FB7185";
const FS = "'Montserrat', system-ui, sans-serif";
const EASE = [0.16, 1, 0.3, 1] as const;

const STATS = [
  { v: "126+", l: "Schools" },
  { v: "19K+", l: "Parents" },
  { v: "2.8B", l: "UGX processed" },
];

const FLOATING_CARDS = [
  { label: "Present", value: "2,361 / 2,486", color: "#3B82F6", delay: 0.7, icon: "check" },
  { label: "Collected", value: "UGX 143.2M", color: "#F59E0B", delay: 0.85, icon: "money" },
  { label: "Buses active", value: "24 routes", color: "#0EA5E9", delay: 1.0, icon: "bus" },
];

function FloatIcon({ type, color, size = 14 }: { type: string; color: string; size?: number }) {
  const props = { width: size, height: size, fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (type === "check") return (<svg viewBox="0 0 24 24" {...props}><path d="M5 12l5 5L20 7" /></svg>);
  if (type === "money") return (<svg viewBox="0 0 24 24" {...props}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>);
  return (<svg viewBox="0 0 24 24" {...props}><path d="M8 6h13l-3 8H6zM6 6L4 8M6 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM17 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" /></svg>);
}

export function HeroVisual() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 0.5], [0, -40]);
  const opacityText = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const yPanel = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden"
      style={{ minHeight: "100svh", background: `linear-gradient(165deg, ${PAPER} 0%, #F0F4F8 40%, #E8EEF4 100%)`, fontFamily: FS }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMouseX(((e.clientX - rect.left) / rect.width - 0.5) * 2);
        setMouseY(((e.clientY - rect.top) / rect.height - 0.5) * 2);
      }}
    >
      {/* Animated gradient mesh */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        animate={{
          background: [
            `radial-gradient(ellipse 60% 50% at 20% 30%, #E8EEF4 0%, transparent 50%)`,
            `radial-gradient(ellipse 60% 50% at 30% 40%, #F4ECD8 0%, transparent 50%)`,
            `radial-gradient(ellipse 60% 50% at 20% 30%, #E8EEF4 0%, transparent 50%)`,
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Ambient blobs with mouse parallax */}
      <motion.div
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        style={{ translateX: mouseX * 20, translateY: mouseY * 20 }}
        className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full opacity-50 blur-3xl"
      >
        <div className="h-full w-full rounded-full" style={{ background: "#E8EEF4" }} />
      </motion.div>
      <motion.div
        animate={{ x: [0, -25, 0], y: [0, 15, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        style={{ translateX: mouseX * -15, translateY: mouseY * -15 }}
        className="pointer-events-none absolute -right-16 bottom-20 h-80 w-80 rounded-full opacity-40 blur-3xl"
      >
        <div className="h-full w-full rounded-full" style={{ background: "#F4ECD8" }} />
      </motion.div>

      {/* Fine grid overlay */}
      <motion.div
        className="pointer-events-none absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `linear-gradient(${INK}33 1px, transparent 1px), linear-gradient(90deg, ${INK}33 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          translateX: mouseX * 8,
          translateY: mouseY * 8,
        }}
      />

      {/* Top gradient fade */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24" style={{ background: `linear-gradient(180deg, ${PAPER}, transparent)` }} />

      {/* Diagonal accent line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.8, duration: 1.2, ease: EASE }}
        className="pointer-events-none absolute left-0 right-0 top-[35%] z-[1] h-px origin-left"
        style={{ background: `linear-gradient(90deg, transparent, ${GOLD}30, transparent)` }}
      />

      <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-4 py-20 sm:px-10 sm:py-16 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-10 lg:px-12 lg:py-20">
        {/* ═══ LEFT — Copy column ═══ */}
        <motion.div style={{ opacity: opacityText, y: yText }} className="relative z-20 max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1, ease: EASE }}
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 sm:mb-5" style={{ borderColor: `${GOLD}40`, background: `${GOLD}0A` }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
              <span className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: GOLD }}>The Digital Operating System for Schools</span>
            </div>

            <h1 style={{ color: INK, fontSize: "clamp(2rem, 8vw, 4.5rem)", fontWeight: 800, lineHeight: 1.0, letterSpacing: "-0.045em" }}>
              <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5, ease: EASE }} className="inline-block">One school.</motion.span>
              <br />
              <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE }} className="inline-block" style={{ color: FOREST_MID }}>One platform.</motion.span>
              <br />
              <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }} className="inline-block" style={{ color: GOLD, fontStyle: "italic" }}>Total visibility.</motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-5 max-w-lg text-sm leading-relaxed sm:mt-6 sm:text-base lg:text-lg"
              style={{ color: MUTED, lineHeight: 1.6 }}
            >
              <span className="sm:hidden">Stop running on paper, spreadsheets and WhatsApp. See your whole school in real time.</span>
              <span className="hidden sm:inline">From the moment a learner applies to the moment they graduate, Fundred connects their entire school journey — while giving management a live view of the entire institution.</span>
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85, ease: EASE }}
            className="mt-6 flex flex-col items-stretch gap-3 sm:mt-7 sm:flex-row sm:items-center"
          >
            <Link
              to="/auth"
              className="group relative inline-flex h-12 items-center justify-center gap-3 overflow-hidden rounded-xl px-6 text-sm font-bold tracking-tight text-white transition-all hover:-translate-y-0.5 hover:shadow-lg sm:h-13 sm:px-7"
              style={{ background: FOREST, boxShadow: "0 10px 24px -12px rgba(10,22,40,0.65)" }}
            >
              <span className="relative z-10">Enter Fundred</span>
              <span className="relative z-10 flex h-6 w-6 items-center justify-center rounded-lg bg-white/15 transition-transform group-hover:translate-x-0.5">
                <ArrowIcon size={15} />
              </span>
              <motion.span className="absolute inset-0 origin-left" initial={{ scaleX: 0 }} whileHover={{ scaleX: 1 }} transition={{ duration: 0.3 }} style={{ background: FOREST_DEEP }} />
            </Link>

          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-6 flex items-center gap-4 border-t pt-5 sm:mt-8 sm:gap-9 sm:pt-6"
            style={{ borderColor: LINE }}
          >
            {STATS.map((stat, index) => (
              <div key={stat.l} className="flex items-center gap-4 sm:gap-9">
                {index > 0 && <div className="h-7 w-px sm:h-8" style={{ background: LINE }} />}
                <div>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.15 + index * 0.1, duration: 0.4 }}
                    className="text-lg font-bold sm:text-2xl"
                    style={{ color: INK, letterSpacing: "-0.04em" }}
                  >
                    {stat.v}
                  </motion.div>
                  <div className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.16em] sm:mt-1 sm:text-[9px]" style={{ color: "#7A8780" }}>{stat.l}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* ═══ RIGHT — Portrait + Control Centre stacked column ═══ */}
        <motion.div
          style={{ y: yPanel, rotateX: mouseY * -2, rotateY: mouseX * 2 }}
          initial={{ opacity: 0, x: 30, scale: 0.97 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="relative z-20 mx-auto mt-8 w-full max-w-md sm:mt-0 sm:max-w-lg lg:ml-auto"
        >
          {/* Decorative shapes — mouse-reactive */}
          <motion.div
            animate={{ scale: [1, 1.15, 1], rotate: [0, 8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            style={{ translateX: mouseX * 25, translateY: mouseY * 25 }}
            className="absolute -bottom-6 -left-6 z-30 h-12 w-12 rounded-full sm:-bottom-8 sm:-left-8 sm:h-16 sm:w-16"
          >
            <div className="h-full w-full rounded-full" style={{ background: GOLD, boxShadow: `0 8px 32px ${GOLD}40` }} />
          </motion.div>
          <motion.div
            animate={{ rotate: [0, -5, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            style={{ translateX: mouseX * -20, translateY: mouseY * -20 }}
            className="absolute -right-6 -top-6 z-30 hidden h-16 w-16 rounded-full border-8 sm:block"
          >
            <div className="h-full w-full rounded-full" style={{ borderColor: `${AQUA}80` }} />
          </motion.div>
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{ translateX: mouseX * 18, translateY: mouseY * 18 }}
            className="absolute -right-4 top-[44%] z-30 hidden h-8 w-8 rounded-full sm:block"
          >
            <div className="h-full w-full rounded-full" style={{ background: `${CORAL}CC`, boxShadow: `0 6px 20px ${CORAL}40` }} />
          </motion.div>

          {/* Glassmorphism glow */}
          <motion.div
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl"
            style={{ background: `linear-gradient(135deg, ${FOREST}20, ${GOLD}15)` }}
          />

          {/* ── Portrait card (girl image) — top of stack ── */}
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6, ease: EASE }}
            className="relative z-20 mb-3 overflow-hidden rounded-2xl border shadow-[0_12px_40px_-15px_rgba(10,22,40,0.3)]"
            style={{ borderColor: `${LINE}80` }}
          >
            <div className="relative h-32 overflow-hidden sm:h-40">
              <motion.img
                src="/images/pexels-daniwura-tci-492293783-33938404 copy.jpg"
                alt="A confident student using the Fundred platform"
                className="h-full w-full object-cover object-[center_20%]"
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Gradient overlay for text readability */}
              <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${FOREST_DEEP}30 0%, transparent 40%, ${FOREST_DEEP}80 100%)` }} />
              {/* Badge on image */}
              <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 backdrop-blur-sm">
                <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="h-1.5 w-1.5 rounded-full" style={{ background: "#34D399" }} />
                <span className="text-[9px] font-bold uppercase tracking-[0.15em]" style={{ color: FOREST_DEEP }}>Live · 9:00 AM</span>
              </div>
            </div>
          </motion.div>

          {/* ── Control Centre Card — below portrait ── */}
          <div
            className="relative z-20 overflow-hidden rounded-3xl border bg-white shadow-[0_20px_60px_-25px_rgba(10,22,40,0.35)]"
            style={{ borderColor: `${LINE}80` }}
          >
            {/* Header bar */}
            <div className="flex items-center justify-between px-5 py-3.5" style={{ background: FOREST_DEEP, color: "#fff" }}>
              <div className="flex items-center gap-2">
                <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="h-2 w-2 rounded-full" style={{ background: "#34D399" }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.9)" }}>School Control Centre</span>
              </div>
              <span className="text-[9px] font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>Today</span>
            </div>

            {/* Body */}
            <div className="space-y-4 p-5 sm:p-6">
              <ControlRow label="School Today">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-black" style={{ color: INK }}>2,486</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>learners</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <PillTag color="#3B82F6" dot label="2,361 present" />
                  <PillTag color="#F59E0B" dot label="87 absent" />
                  <PillTag color="#FB7185" dot label="38 exceptions" />
                </div>
              </ControlRow>

              <DividerLine />

              <ControlRow label="Money">
                <div className="grid grid-cols-3 gap-2">
                  <StatBox label="Billed" value="184.6M" color={FOREST} />
                  <StatBox label="Collected" value="143.2M" color="#2F7E67" />
                  <StatBox label="Outstanding" value="41.4M" color="#D9A441" />
                </div>
              </ControlRow>

              <DividerLine />

              <ControlRow label="Operations">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <OpItem label="Buses active" value="24" color="#0EA5E9" />
                  <OpItem label="Procurement" value="2" color={FOREST_MID} />
                  <OpItem label="Maintenance" value="6" color="#D9A441" />
                  <OpItem label="Welfare cases" value="3" color="#CC746A" />
                </div>
              </ControlRow>

              <DividerLine />

              <div>
                <div className="mb-2 flex items-center gap-2">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 9v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                  </svg>
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: GOLD }}>Today's Attention</span>
                </div>
                <div className="space-y-1.5">
                  <AttentionItem text="12 parents have overdue balances requiring follow-up" />
                  <AttentionItem text="Food stock projected to run short in 4 days" />
                  <AttentionItem text="Bus 08 has a maintenance alert" />
                  <AttentionItem text="P4 Mathematics attendance has fallen below target" />
                </div>
              </div>
            </div>
          </div>

          {/* ── Floating live stat cards — positioned around the stack ── */}
          {FLOATING_CARDS.map((card, i) => {
            const positions = [
              { top: "-12px", right: "12px" },
              { top: "38%", right: "-16px" },
              { bottom: "20px", right: "-10px" },
            ];
            const pos = positions[i] || positions[0];
            return (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: card.delay, duration: 0.5, ease: EASE }}
                style={{
                  ...pos,
                  translateX: mouseX * 12,
                  translateY: mouseY * 12,
                }}
                className="absolute z-40 flex items-center gap-2.5 rounded-2xl border border-white/80 bg-white/95 px-3 py-2.5 shadow-[0_12px_30px_-15px_rgba(16,21,17,0.3)] backdrop-blur-md sm:px-4 sm:py-3"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-lg sm:h-8 sm:w-8" style={{ background: `${card.color}15` }}>
                  <FloatIcon type={card.icon} color={card.color} />
                </div>
                <div>
                  <div className="text-[10px] font-bold sm:text-xs" style={{ color: INK }}>{card.label}</div>
                  <div className="text-[8px] font-medium sm:text-[10px]" style={{ color: MUTED }}>{card.value}</div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="flex flex-col items-center gap-1">
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: "#7A8780" }}>Scroll</span>
          <div className="h-8 w-px" style={{ background: `${INK}20` }} />
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: `linear-gradient(to bottom, transparent, ${PAPER})` }} />
    </section>
  );
}

function ControlRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: MUTED }}>{label}</p>
      {children}
    </div>
  );
}

function DividerLine() {
  return <div className="h-px w-full" style={{ background: `${LINE}60` }} />;
}

function PillTag({ color, dot, label }: { color: string; dot?: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold" style={{ background: `${color}12`, color }}>
      {dot && <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />}
      {label}
    </span>
  );
}

function StatBox({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-xl p-2.5" style={{ background: `${color}08`, border: `1px solid ${color}18` }}>
      <p className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>{label}</p>
      <p className="mt-0.5 text-sm font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

function OpItem({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px]" style={{ color: MUTED }}>{label}</span>
      <span className="text-[11px] font-bold" style={{ color }}>{value}</span>
    </div>
  );
}

function AttentionItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1 h-1 w-1 shrink-0 rounded-full" style={{ background: GOLD }} />
      <p className="text-[11px] leading-relaxed" style={{ color: INK }}>{text}</p>
    </div>
  );
}
